import React, { memo, useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { TableCell, TableRow } from '../../ui/Table'
import Checkbox from '../../ui/Checkbox'
import Text from '../../ui/Text'
import Link from '../../ui/Link'
import Badge from '../../ui/Badge'
import { formatDateTime } from '../../../utils/dateHelpers'
import StatusDot from '../IssueStatuses/StatusDot'
import { categoryBadgeVariant, categoryLabel } from '../IssueStatuses/constants'
import AttributionCell from './AttributionCell'
import ContentCell from './ContentCell'
import RowActionsMenu from './RowActionsMenu'
import EllipsisIcon from '../../ui/icons/EllipsisIcon'
import { PRIMARY_COLUMN_KEYS, EXTRA_COLUMN_TYPES, orderedColumnSlots } from './constants'
import { DATA_TABLE_ACTIONS_COL_CLASS, DATA_TABLE_ACTIONS_STICKY_CLASS } from '../../ui/DataTable/styles'

const ID_LINK_CLASS = 'tw:text-gray-900 tw:underline tw:cursor-pointer '
  + 'tw:bg-transparent tw:border-0 tw:p-0'
const ID_HASH_CLASS = 'tw:text-gray-400'
// Hover affordance lives on the cell (ring inset, no layout shift), not the badge.
// The row already paints the bg gray-100; the gray-400 ring marks the clickable cell.
const EDITABLE_CELL_CLASS = 'tw:cursor-pointer tw:hover:ring-1 tw:hover:ring-inset tw:hover:ring-gray-400'
const EDITABLE_CELL_DISABLED_CLASS = 'tw:cursor-progress tw:opacity-60'
const EDITABLE_BLOCK_ANCHOR_CLASS = 'tw:block tw:w-40 tw:rounded! tw:px-1.5 tw:py-0.5'
const resolveRowBg = (selected, active) => {
  if (selected || active)
    return 'tw:bg-gray-100!'
  return 'tw:hover:bg-gray-100!'
}
const resolveActionsBg = (selected, active, baseBg) => {
  if (selected || active)
    return 'tw:bg-gray-100!'
  return `${baseBg} tw:group-hover:bg-gray-100!`
}
const ACTIONS_BTN_BASE = 'tw:bg-transparent tw:border-0 tw:cursor-pointer '
  + 'tw:text-gray-500 tw:rounded! tw:hover:bg-gray-200 tw:opacity-0 '
  + 'tw:group-hover:opacity-100 tw:focus-visible:opacity-100 '
  + 'tw:w-10 tw:h-10 tw:inline-flex tw:items-center tw:justify-center tw:flex-shrink-0'

const EXTRA_VALUE_PLACEHOLDER = '--'
const EMPTY_COLUMNS = []

const TriggeredAlertsRow = ({
  disabled = false,
  issue,
  primaryColumns = EMPTY_COLUMNS,
  extraColumns = EMPTY_COLUMNS,
  selected = false,
  hasOpenPopup = false,
  atScrollEnd = true,
  onToggleRow,
  onAttributionEdit, onIssueClick, onShareClick, onStatusEdit, onExportPdf
}) => {
  const handleToggle = useCallback(() => onToggleRow(issue.id), [issue.id, onToggleRow])
  const statusCellRef = useRef(null)
  const attributionCellRef = useRef(null)
  // Capture the trigger from the click event, not a ref: the button remounts when columns change.
  const [actionsBtnEl, setActionsBtnEl] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const handleIdClick = useCallback(() => onIssueClick(issue), [onIssueClick, issue])
  const handleStatusClick = useCallback(() => {
    if (disabled)
      return
    onStatusEdit(issue, statusCellRef.current)
  }, [disabled, onStatusEdit, issue])
  const handleAttributionClick = useCallback(() => {
    if (disabled)
      return
    onAttributionEdit(issue, attributionCellRef.current)
  }, [disabled, onAttributionEdit, issue])

  const handleMenuToggle = useCallback(event => {
    setActionsBtnEl(event.currentTarget)
    setMenuOpen(prev => !prev)
  }, [])
  const handleMenuClose = useCallback(() => setMenuOpen(false), [])
  const handleMenuEdit = useCallback(() => onIssueClick(issue), [onIssueClick, issue])
  const handleMenuShare = useCallback(() => onShareClick(issue), [onShareClick, issue])
  const handleMenuExportPdf = useCallback(() => onExportPdf(issue), [onExportPdf, issue])

  const editableClass = disabled ? EDITABLE_CELL_DISABLED_CLASS : EDITABLE_CELL_CLASS

  const isActive = menuOpen || hasOpenPopup
  const rowClass = `tw:group ${resolveRowBg(selected, isActive)}`
  const actionsBtnClass = `${ACTIONS_BTN_BASE}${isActive ? ' tw:opacity-100!' : ''}`

  const renderIdCell = () => (
    <TableCell key={PRIMARY_COLUMN_KEYS.ID}>
      <button className={ID_LINK_CLASS} type="button" onClick={handleIdClick}>
        <span className={ID_HASH_CLASS}>#</span>
        {issue.id}
      </button>
    </TableCell>
  )

  const renderAlertNameCell = () => {
    const key = PRIMARY_COLUMN_KEYS.ALERT_NAME
    return (
      <TableCell key={key}>
        <Text className="tw:break-words" lines={2}>{issue.name}</Text>
      </TableCell>
    )
  }

  const renderStatutCell = () => (
    <TableCell
      key={PRIMARY_COLUMN_KEYS.STATUT}
      className={editableClass}
      onClick={handleStatusClick}
    >
      <span ref={statusCellRef} className={EDITABLE_BLOCK_ANCHOR_CLASS}>
        <span className="tw:flex tw:items-center tw:gap-2">
          <StatusDot color={issue.status_color} />
          <span className="tw:flex-1 tw:min-w-0">
            <Text className="tw:block tw:text-gray-900" truncate>{issue.status_name}</Text>
          </span>
        </span>
      </span>
    </TableCell>
  )

  const renderCategorieCell = () => (
    <TableCell key={PRIMARY_COLUMN_KEYS.CATEGORIE}>
      <Badge variant={categoryBadgeVariant(issue.status_category)}>
        {categoryLabel(issue.status_category)}
      </Badge>
    </TableCell>
  )

  const renderCreatedAtCell = () => (
    <TableCell key={PRIMARY_COLUMN_KEYS.CREATED_AT}>
      {formatDateTime(issue.created_at)}
    </TableCell>
  )

  const renderAttributionCell = () => (
    <TableCell
      key={PRIMARY_COLUMN_KEYS.ATTRIBUTION}
      className={editableClass}
      onClick={handleAttributionClick}
    >
      <span ref={attributionCellRef} className={EDITABLE_BLOCK_ANCHOR_CLASS}>
        <AttributionCell users={issue.attribution} />
      </span>
    </TableCell>
  )

  const renderResourceNameCell = () => (
    <TableCell key={PRIMARY_COLUMN_KEYS.RESOURCE_NAME}>
      <Link href={issue.resource.path} lines={2} target="_blank">
        {issue.resource.name}
      </Link>
    </TableCell>
  )

  const renderContentCell = () => (
    <TableCell key={PRIMARY_COLUMN_KEYS.CONTENT}>
      <ContentCell
        comment={issue.comment}
        hasComment={issue.has_comment}
        photosCount={issue.photos_count}
      />
    </TableCell>
  )

  const cellRenderers = {
    [PRIMARY_COLUMN_KEYS.ID]: renderIdCell,
    [PRIMARY_COLUMN_KEYS.ALERT_NAME]: renderAlertNameCell,
    [PRIMARY_COLUMN_KEYS.STATUT]: renderStatutCell,
    [PRIMARY_COLUMN_KEYS.CATEGORIE]: renderCategorieCell,
    [PRIMARY_COLUMN_KEYS.CREATED_AT]: renderCreatedAtCell,
    [PRIMARY_COLUMN_KEYS.ATTRIBUTION]: renderAttributionCell,
    [PRIMARY_COLUMN_KEYS.RESOURCE_NAME]: renderResourceNameCell,
    [PRIMARY_COLUMN_KEYS.CONTENT]: renderContentCell
  }

  const readExtraValue = column => {
    if (column.type === EXTRA_COLUMN_TYPES.VARIABLE)
      return issue.extra_variable_values?.[column.id]
    return issue.extra_values?.[column.id]
  }

  const renderExtraCell = column => {
    const key = `extra-${column.type}-${column.id}`
    const value = readExtraValue(column)
    // Missing answer (null/undefined/blank string) shows the placeholder; a real 0 renders as-is.
    if ((value ?? '') === '') {
      return (
        <TableCell key={key}>
          <span className="tw:text-gray-500">{EXTRA_VALUE_PLACEHOLDER}</span>
        </TableCell>
      )
    }
    return (
      <TableCell key={key}>
        <Text className="tw:break-words" lines={2}>{value}</Text>
      </TableCell>
    )
  }

  // Dedicated actions cell, sticky-right so the "…" trigger stays visible while the table
  // Scrolls horizontally. Transparent while scrolling (the row content shows through); takes
  // The row bg otherwise — white at rest, gray-100 on hover / open menu / selection.
  const baseBgClass = atScrollEnd ? 'tw:bg-white!' : 'tw:bg-transparent!'
  const actionsBgClass = resolveActionsBg(selected, isActive, baseBgClass)
  const actionsCellClass = `${DATA_TABLE_ACTIONS_COL_CLASS} ${DATA_TABLE_ACTIONS_STICKY_CLASS} `
    + `tw:border-l-0! tw:text-center tw:align-middle! ${actionsBgClass}`

  return (
    <TableRow className={rowClass}>
      <TableCell>
        <Checkbox
          aria-label={`Sélectionner alerte ${issue.id}`}
          checked={selected}
          onChange={handleToggle}
        />
      </TableCell>
      {orderedColumnSlots(primaryColumns, extraColumns).map(slot => {
        if (slot.kind === 'primary')
          return cellRenderers[slot.key]()
        return renderExtraCell(slot.column)
      })}
      <TableCell className={actionsCellClass}>
        <button
          aria-haspopup="menu"
          aria-label={`Actions pour l’alerte ${issue.id}`}
          className={actionsBtnClass}
          type="button"
          onClick={handleMenuToggle}
        >
          <EllipsisIcon className="tw:w-4 tw:h-4" />
        </button>
        <RowActionsMenu
          anchorEl={actionsBtnEl}
          isOpen={menuOpen}
          onClose={handleMenuClose}
          onEdit={handleMenuEdit}
          onExportPdf={handleMenuExportPdf}
          onShare={handleMenuShare}
        />
      </TableCell>
    </TableRow>
  )
}

TriggeredAlertsRow.propTypes = {
  atScrollEnd: PropTypes.bool,
  disabled: PropTypes.bool,
  extraColumns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  })),
  issue: PropTypes.shape({
    comment: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  onAttributionEdit: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  onIssueClick: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  onStatusEdit: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  primaryColumns: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.bool
}

TriggeredAlertsRow.defaultProps = {
  atScrollEnd: true,
  disabled: false,
  extraColumns: EMPTY_COLUMNS,
  primaryColumns: EMPTY_COLUMNS,
  selected: false
}

export default memo(TriggeredAlertsRow)
